import { useUIStore } from '@/config/stores/ui-store';
import { useConversationMembers } from '@/features/conversation/queries/member.queries';
import { MessageContentType } from '@/features/messages/models/message-content-type';
import { cn } from '@/shared/utilities';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import CloseIcon from '@mui/icons-material/Close';
import ImageIcon from '@mui/icons-material/Image';
import ReplyIcon from '@mui/icons-material/Reply';
import { Box, IconButton, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';

interface MessageInputReplyProps {
  convId: Nullable<string>;
}

export const MessageInputReply: React.FC<MessageInputReplyProps> = ({ convId }) => {
  const { t } = useTranslation();
  const { clearReplyMessage } = useUIStore();
  const convKey = convId || '';
  const replyMsg = useUIStore((s) => s.replyMessages[convKey]);
  const { data: members } = useConversationMembers(convKey);
  if (!replyMsg) return null;

  const getContentPreview = () => {
    switch (replyMsg.contentType) {
      case MessageContentType.TEXT:
        return replyMsg.content;
      case MessageContentType.IMAGE:
        return t('messages.reply.mediaTypes.image');
      case MessageContentType.FILE:
        return t('messages.reply.mediaTypes.file');
      case MessageContentType.AUDIO:
        return t('messages.reply.mediaTypes.audio');
      case MessageContentType.VIDEO:
        return t('messages.reply.mediaTypes.video');
      case MessageContentType.COMPOSE:
      case MessageContentType.COMBINE:
        return t('messages.reply.mediaTypes.multimedia');
      default:
        return replyMsg.content;
    }
  };

  const getContentIcon = () => {
    switch (replyMsg.contentType) {
      case MessageContentType.IMAGE:
        return <ImageIcon sx={{ fontSize: 16, color: 'text.secondary' }} />;
      case MessageContentType.FILE:
      case MessageContentType.AUDIO:
      case MessageContentType.VIDEO:
        return <AttachFileIcon sx={{ fontSize: 16, color: 'text.secondary' }} />;
      default:
        return null;
    }
  };

  return (
    <Box
      className={cn(
        'mex:mb-2 mex:flex mex:items-center mex:gap-3 mex:rounded-t-lg mex:border mex:border-divider mex:border-l-4 mex:border-l-primary-main mex:bg-background-paper mex:p-1'
      )}
      sx={{
        backgroundColor: 'background.paper',
        borderColor: 'divider',
        borderLeftColor: 'primary.main'
      }}
    >
      <ReplyIcon sx={{ fontSize: 18, color: 'primary.main' }} />

      <Box className='mex:min-w-0 mex:flex-1'>
        <Typography
          variant='caption'
          sx={{
            color: 'primary.main',
            fontWeight: 'medium',
            display: 'block'
          }}
        >
          {t('messages.reply.to', {
            name:
              (members || []).find((m) => m.userId === replyMsg.senderId)?.user?.fullName ||
              t('messages.reply.unknownUser')
          })}
        </Typography>

        <Box className='mex:mt-1 mex:flex mex:items-center mex:gap-1'>
          {getContentIcon()}
          <Typography
            variant='body2'
            sx={{
              color: 'text.secondary',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              maxWidth: '200px'
            }}
          >
            {getContentPreview()}
          </Typography>
        </Box>
      </Box>

      <IconButton
        size='small'
        onClick={() => clearReplyMessage(convId || '')}
        sx={{
          color: 'text.secondary',
          '&:hover': {
            backgroundColor: 'action.hover'
          }
        }}
      >
        <CloseIcon sx={{ fontSize: 16 }} />
      </IconButton>
    </Box>
  );
};
